import React, { useMemo, useState } from "react";
import { formatUSD } from "@/lib/api";
import { Calculator } from "lucide-react";

export default function FinancingPage() {
  const [price, setPrice] = useState(150000);
  const [down, setDown] = useState(20000);
  const [rate, setRate] = useState(8.5);
  const [term, setTerm] = useState(60);

  const { monthly, total, interest } = useMemo(() => {
    const principal = Math.max(price - down, 0);
    const r = rate / 100 / 12;
    const n = term;
    const m = r === 0 ? principal / n : (principal * r) / (1 - Math.pow(1 + r, -n));
    const t = m * n + down;
    return {
      monthly: isFinite(m) ? m : 0,
      total: isFinite(t) ? t : price,
      interest: isFinite(m) ? m * n - principal : 0,
    };
  }, [price, down, rate, term]);

  return (
    <div data-testid="financing-page" className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10">
      <div className="border-b border-white/10 pb-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Financing</div>
        <h1 className="mt-4 font-display text-5xl font-black uppercase leading-none text-white sm:text-6xl">
          Finance calculator
        </h1>
        <p className="mt-4 max-w-2xl text-base text-white/60">
          Estimate monthly payments on any machine in our inventory. Final rates vary by credit, equipment age, and term. We partner with 12+ lenders including CAT Financial, John Deere Financial, and independent commercial lenders across Texas.
        </p>
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        {/* INPUTS */}
        <div className="lg:col-span-7">
          <div className="border border-[#27272A] bg-[#141415]">
            <div className="flex items-center gap-3 border-b border-[#27272A] p-6">
              <Calculator className="h-5 w-5 text-[#FFB800]" />
              <h2 className="font-display text-xl font-bold uppercase">Loan Parameters</h2>
            </div>
            <div className="space-y-8 p-6">
              <SliderField label="Equipment Price" value={price} min={10000} max={1000000} step={1000} onChange={setPrice} format={formatUSD} testid="finance-price" />
              <SliderField label="Down Payment" value={down} min={0} max={Math.min(price, 500000)} step={1000} onChange={setDown} format={formatUSD} testid="finance-down" />
              <SliderField label="Interest Rate (APR)" value={rate} min={3} max={15} step={0.1} onChange={setRate} format={(v) => `${v.toFixed(2)}%`} testid="finance-rate" />
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Term</label>
                  <span className="font-display text-2xl font-black text-[#FFB800]">{term} mo</span>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {[24, 36, 48, 60, 72].map((t) => (
                    <button
                      key={t}
                      onClick={() => setTerm(t)}
                      data-testid={`finance-term-${t}`}
                      className={`border p-3 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
                        term === t
                          ? "border-[#FFB800] bg-[#FFB800] text-black"
                          : "border-[#27272A] text-white/70 hover:border-[#FFB800]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RESULTS */}
        <div className="lg:col-span-5">
          <div className="sticky top-24 border border-[#27272A] bg-[#141415]">
            <div className="border-b border-[#27272A] bg-[#0a0a0b] p-8 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Estimated Monthly Payment</div>
              <div data-testid="finance-monthly" className="mt-4 font-display text-6xl font-black text-[#FFB800] sm:text-7xl">
                {formatUSD(monthly)}
              </div>
              <div className="mt-2 font-mono text-xs uppercase tracking-wider text-white/50">/ month × {term} months</div>
            </div>
            <div className="divide-y divide-[#27272A] p-2">
              <Row label="Amount Financed" value={formatUSD(price - down)} />
              <Row label="Total Interest" value={formatUSD(interest)} />
              <Row label="Total of Payments" value={formatUSD(total)} emphasis />
            </div>
            <div className="border-t border-[#27272A] p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                Estimate only. Not a formal credit offer.
              </p>
              <a
                href="/contact"
                className="mt-4 flex items-center justify-center bg-[#FFB800] px-6 py-4 font-display text-sm font-bold uppercase tracking-widest text-black hover:bg-[#E5A600]"
              >
                Apply for Financing
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const SliderField = ({ label, value, min, max, step, onChange, format, testid }) => (
  <div>
    <div className="mb-3 flex items-center justify-between">
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</label>
      <span className="font-display text-2xl font-black text-[#FFB800]">{format(value)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="w-full accent-[#FFB800]"
    />
    <div className="mt-1 flex justify-between font-mono text-[10px] text-white/30">
      <span>{format(min)}</span>
      <span>{format(max)}</span>
    </div>
  </div>
);

const Row = ({ label, value, emphasis }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</span>
    <span className={`font-display font-bold ${emphasis ? "text-xl text-white" : "text-sm text-white/90"}`}>{value}</span>
  </div>
);
